// Stores list (WEB_PLAN.md §5.5, route /stores): port of GroundTeam/Views/MapTab/RecordListView.swift and
// MapTab+Store.filteredRecords as a virtualized table. Read-only in Phase 1.
//
// Pipeline, every step memoized on its inputs so typing in search does not redo the filter and a sync that touches
// one activity does not rebuild the columns:
//   visible stores (archived with the toggle) → rows with derived stats → applyFilter (MapFilter, from the URL)
//   → searchRecords (deferred, so typing stays smooth on 10,000 stores) → the iOS sort or the header sort.
// The same rows, in the same order and with the visible columns, are what "Export CSV" writes.
//
// Everything but the search text is in the URL (listState.ts), so Dashboard tiles link to a filtered list and Back
// from a store returns to the same view. Reps get "My stores" (their visible stores, no owner filter, no export).

import { Archive, ArrowUpDown, Columns3, Download, RotateCcw, SlidersHorizontal, Upload } from 'lucide-react';
import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useSession } from '@/auth/AuthProvider';
import type { LeadSourcePayload, TerritoryPayload } from '@/contracts/models';
import { idKey } from '@/contracts/uuid';
import { useLeadSources, useTerritories, useViewer, useVisibleRecords, useVisitResults } from '@/data/hooks';
import type { DataList } from '@/data/lists';
import { formatGrouped } from '@/domain/format';
import {
  EMPTY_FILTER,
  FILTER_PRESETS,
  FILTER_PRESET_COLORS,
  FILTER_PRESET_TITLES,
  RECORD_LIST_SORT_TITLES,
  applyFilter,
  filterActiveCount,
  isPresetOn,
  searchRecords,
  sortRecords,
  togglePreset,
  type FilterContext,
  type MapFilter,
} from '@/domain/records';
import {
  NoMatches,
  NoStoresYet,
  RequireFirstSync,
  ScreenLoading,
  useDocumentTitle,
  useStoreRowLookups,
} from '@/features/common';
import { useCan } from '@/session/permissions';
import {
  Badge,
  Button,
  Chip,
  DropdownMenu,
  EmptyState,
  PageContainer,
  PageHeader,
  SearchField,
  Switch,
  useTicker,
  useToast,
  type MenuEntry,
} from '@/ui';
import { loadColumnPrefs, saveColumnPrefs, isColumnVisible, setColumnChoice, visibleColumns, type ColumnChoices } from './columnPrefs';
import {
  COLUMN_GROUP_TITLES,
  allColumns,
  buildRows,
  exportTable,
  sortRowsByColumns,
  type ColumnGroup,
  type ColumnSort,
  type StoreColumnContext,
  type StoreListRow,
} from './columns';
import { csvFileName, toCsv } from './csv';
import { FilterPanel } from './FilterPanel';
import { ImportStoresDialog } from './import/ImportStoresDialog';
import { NO_STORES_IMPORT_TEXT } from './import/importModel';
import {
  LIST_SORTS,
  capColumnSorts,
  listStateFromQuery,
  listStateToQuery,
  loadSearch,
  saveSearch,
  sameQuery,
  sortDescription,
  type ListSort,
  type StoresListState,
} from './listState';
import { StoresTable } from './StoresTable';

export default function StoresPage() {
  const can = useCan();
  const title = can.isRep ? 'My stores' : 'Stores';
  useDocumentTitle(title);
  return (
    <RequireFirstSync
      what={title}
      fallback={
        <PageContainer>
          <ScreenLoading layout="table" label="Loading stores…" />
        </PageContainer>
      }
    >
      <StoresList title={title} />
    </RequireFirstSync>
  );
}

// Storage may be blocked entirely (the getter itself throws in some privacy modes).
function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
function localStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Column choices, saved per browser. */
function useColumnChoices(): [ColumnChoices, (next: ColumnChoices) => void] {
  const [choices, setChoices] = useState<ColumnChoices>(() => loadColumnPrefs(localStore()));
  const update = useCallback((next: ColumnChoices) => {
    setChoices(next);
    saveColumnPrefs(localStore(), next);
  }, []);
  return [choices, update];
}

/** Payloads by lower-case id (territory and source names for cells, sorting and the export). */
function usePayloadMap<T>(list: DataList<T>): ReadonlyMap<string, T> {
  return useMemo(() => new Map(list.items.map((i) => [idKey(i.id), i.value])), [list]);
}

/** Starts a download of a text file built in the page. */
function downloadText(text: string, fileName: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a moment to start reading the blob before it is released.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function StoresList({ title }: { title: string }) {
  const can = useCan();
  const session = useSession();
  const viewer = useViewer();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // MARK: List state (URL) and search (per tab)

  const [params, setParams] = useSearchParams();
  const state = useMemo(() => listStateFromQuery(params), [params]);
  const setState = useCallback(
    (next: StoresListState) => {
      const q = listStateToQuery(next);
      if (!sameQuery(q, params)) setParams(q, { replace: true });
    },
    [params, setParams],
  );
  const setFilter = useCallback((filter: MapFilter) => setState({ ...state, filter }), [state, setState]);

  const [query, setQuery] = useState(() => loadSearch(sessionStore()));
  useEffect(() => saveSearch(sessionStore(), query), [query]);
  const deferredQuery = useDeferredValue(query);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // MARK: Data

  const active = useVisibleRecords();
  const withArchived = useVisibleRecords({ includeArchived: true });
  const list = state.archived ? withArchived : active;
  const archivedCount = withArchived.items.length - active.items.length;
  const lookups = useStoreRowLookups();
  const visitResults = useVisitResults();
  const territories = usePayloadMap<TerritoryPayload>(useTerritories());
  const sources = usePayloadMap<LeadSourcePayload>(useLeadSources());
  // "Overdue" and "follow-up due by today" move with the clock, not only with the data.
  const minute = useTicker(60_000);
  const now = useMemo(() => {
    void minute;
    return new Date();
  }, [minute]);

  const ctx = useMemo<StoreColumnContext>(() => ({ lookups, territories, sources, now }), [lookups, territories, sources, now]);
  const specs = useMemo(() => allColumns(lookups.customFields), [lookups.customFields]);
  const specById = useMemo(() => new Map(specs.map((s) => [s.id, s])), [specs]);
  const [choices, setChoices] = useColumnChoices();
  const shown = useMemo(() => visibleColumns(specs, choices), [specs, choices]);

  const rows = useMemo(() => buildRows(list.items, lookups.stats), [list, lookups.stats]);
  const rowOf = useMemo(() => new Map(rows.map((r) => [r.record, r])), [rows]);
  const records = useMemo(() => rows.map((r) => r.record), [rows]);

  const filterCtx = useMemo<FilterContext>(
    () => ({ stats: lookups.stats, pipelines: lookups.pipelines, viewerId: viewer?.profileId, now }),
    [lookups.stats, lookups.pipelines, viewer?.profileId, now],
  );
  const filtered = useMemo(() => applyFilter(records, state.filter, filterCtx), [records, state.filter, filterCtx]);
  const searched = useMemo(() => searchRecords(filtered, deferredQuery), [filtered, deferredQuery]);

  // Header sorts on columns that no longer exist (a custom field removed) are ignored, falling back to the iOS sort.
  const columnSort = useMemo(() => state.columnSort.filter((c) => specById.has(c.id)), [state.columnSort, specById]);
  const sortedRows = useMemo<StoreListRow[]>(() => {
    if (columnSort.length > 0) {
      return sortRowsByColumns(
        searched.map((r) => rowOf.get(r)!),
        columnSort,
        specById,
        ctx,
      );
    }
    return sortRecords(searched, state.sort, { stats: lookups.stats, pipelines: lookups.pipelines, now: lookups.now }).map(
      (r) => rowOf.get(r)!,
    );
  }, [searched, rowOf, columnSort, specById, ctx, state.sort, lookups.stats, lookups.pipelines, lookups.now]);

  // MARK: Actions

  const filterCount = filterActiveCount(state.filter);
  const count = sortedRows.length;
  const sortText = sortDescription({ sort: state.sort, columnSort }, (id) => specById.get(id)?.title);

  // As iOS RecordListView: the button resets the filters only (the search has its own clear button) and only shows
  // when a filter is on.
  const resetFilters = () => setFilter({ ...EMPTY_FILTER });

  const hrefFor = useCallback((row: StoreListRow) => `/stores/${idKey(row.item.id)}`, []);
  // The detail page's "Stores" link comes back to this exact view.
  const linkState = useMemo(() => ({ listSearch: location.search }), [location.search]);
  const open = useCallback(
    (row: StoreListRow, newTab: boolean) => {
      const href = hrefFor(row);
      if (newTab) window.open(href, '_blank', 'noopener');
      else navigate(href, { state: linkState });
    },
    [hrefFor, navigate, linkState],
  );

  const exportCsv = () => {
    const table = exportTable(sortedRows, shown, ctx);
    downloadText(toCsv(table.header, table.rows), csvFileName(session.companyName), 'text/csv;charset=utf-8');
    toast.success(`Exported ${formatGrouped(count)} store${count === 1 ? '' : 's'} to CSV.`);
  };

  const sortItems = useMemo<MenuEntry[]>(
    () =>
      LIST_SORTS.map((s: ListSort) => ({
        id: s,
        label: RECORD_LIST_SORT_TITLES[s],
        checked: columnSort.length === 0 && state.sort === s,
        onSelect: () => setState({ ...state, sort: s, columnSort: [] }),
      })),
    [columnSort.length, state, setState],
  );

  const columnItems = useMemo<MenuEntry[]>(() => {
    const out: MenuEntry[] = [];
    for (const group of Object.keys(COLUMN_GROUP_TITLES) as ColumnGroup[]) {
      const inGroup = specs.filter((s) => s.group === group);
      if (inGroup.length === 0) continue;
      if (out.length > 0) out.push({ type: 'separator', id: `sep-${group}` });
      out.push({ type: 'label', id: `label-${group}`, label: COLUMN_GROUP_TITLES[group] });
      for (const spec of inGroup) {
        const visible = isColumnVisible(spec, choices);
        out.push({
          id: spec.id,
          label: spec.title,
          checked: visible,
          disabled: !spec.hideable,
          keepOpen: true,
          onSelect: () => setChoices(setColumnChoice(choices, spec, !visible)),
        });
      }
    }
    out.push({ type: 'separator', id: 'sep-reset' });
    out.push({
      id: 'reset',
      label: 'Reset columns',
      icon: <RotateCcw />,
      disabled: Object.keys(choices).length === 0,
      onSelect: () => setChoices({}),
    });
    return out;
  }, [specs, choices, setChoices]);

  // MARK: Empty states

  const allArchived = !state.archived && archivedCount > 0;
  const emptyState =
    list.items.length === 0 ? (
      <NoStoresYet
        compact
        message={
          allArchived
            ? `Every store you can see is archived (${formatGrouped(archivedCount)}). Turn on "Show archived" to list them.`
            : can.canCreateRecords
              ? NO_STORES_IMPORT_TEXT
              : undefined
        }
        action={
          !allArchived && can.canCreateRecords ? (
            <Button variant="primary" icon={<Upload />} onClick={() => setImportOpen(true)}>
              Import stores
            </Button>
          ) : undefined
        }
      />
    ) : filterCount > 0 || deferredQuery.trim() !== '' ? (
      <NoMatches compact onReset={filterCount > 0 ? resetFilters : undefined} />
    ) : (
      <EmptyState compact title="Nothing here" message="Stores you can see will be listed here." />
    );

  return (
    <PageContainer className="flex h-full min-h-[560px] flex-col">
      <PageHeader
        title={title}
        subtitle={
          <span aria-live="polite">
            <span className="font-semibold text-fg tabular-nums">
              {formatGrouped(count)} result{count === 1 ? '' : 's'}
            </span>
            <span> · Sorted by {sortText}</span>
          </span>
        }
        actions={
          <>
            <DropdownMenu
              label="Columns"
              trigger={
                <Button variant="secondary" icon={<Columns3 />}>
                  Columns
                </Button>
              }
              items={columnItems}
              minWidth={240}
            />
            {can.canExportCsv && (
              <Button variant="secondary" icon={<Download />} onClick={exportCsv} disabled={count === 0}>
                Export CSV
              </Button>
            )}
            {can.canCreateRecords && (
              <Button variant="secondary" icon={<Upload />} onClick={() => setImportOpen(true)}>
                Import
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-3 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchField
            value={query}
            onValueChange={setQuery}
            label="Search name, address, phone, tag"
            slashShortcut
            className="w-full min-w-[220px] flex-1 sm:max-w-md"
          />
          <DropdownMenu
            label="Sort by"
            trigger={
              <Button variant="secondary" icon={<ArrowUpDown />} aria-label={`Sort, currently ${sortText}`}>
                Sort
              </Button>
            }
            items={sortItems}
            minWidth={280}
          />
          <Button
            variant="secondary"
            icon={<SlidersHorizontal />}
            onClick={() => setFiltersOpen(true)}
            aria-label={filterCount > 0 ? `Filters, ${filterCount} active` : 'Filters'}
          >
            Filters
            {filterCount > 0 && (
              <Badge tone="danger" variant="solid" size="xs" className="ml-0.5 tabular-nums">
                {filterCount}
              </Badge>
            )}
          </Button>
          {filterCount > 0 && (
            <Button variant="ghost" icon={<RotateCcw />} onClick={resetFilters}>
              Reset filters
            </Button>
          )}
          <span className="flex-1" />
          <Switch
            label={
              <span className="inline-flex items-center gap-1.5">
                <Archive aria-hidden className="size-4 text-fg-muted" />
                Show archived{archivedCount > 0 ? ` (${formatGrouped(archivedCount)})` : ''}
              </span>
            }
            checked={state.archived}
            onCheckedChange={(v) => setState({ ...state, archived: v })}
            size="sm"
          />
        </div>
        <div role="group" aria-label="Quick filters" className="flex flex-wrap gap-2">
          {FILTER_PRESETS.map((p) => (
            <Chip
              key={p}
              color={FILTER_PRESET_COLORS[p]}
              selected={isPresetOn(state.filter, p, visitResults.values)}
              onClick={() => setFilter(togglePreset(state.filter, p, visitResults.values))}
            >
              {FILTER_PRESET_TITLES[p]}
            </Chip>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <StoresTable
          rows={sortedRows}
          columns={shown}
          ctx={ctx}
          columnSort={columnSort}
          onColumnSortChange={(next: ColumnSort[]) => setState({ ...state, columnSort: capColumnSorts(next) })}
          hrefFor={hrefFor}
          onOpen={open}
          emptyState={emptyState}
          linkState={linkState}
        />
      </div>

      <FilterPanel
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filter={state.filter}
        onApply={setFilter}
        records={records}
        filterContext={filterCtx}
        isManager={can.isManager}
      />

      {can.canCreateRecords && <ImportStoresDialog open={importOpen} onClose={() => setImportOpen(false)} />}
    </PageContainer>
  );
}
